import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import api, { formatApiError } from "../../lib/api";

const PHONE_REGEX = /^\+?[0-9\s\-()]{10,17}$/;

export default function ApplyForm() {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", tiktok_username: "", why_us: "", kvkk: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Ad Soyad alanı zorunludur";
    if (!form.phone.trim()) e.phone = "Telefon numarası zorunludur";
    else if (!PHONE_REGEX.test(form.phone.trim())) e.phone = "Geçerli bir telefon numarası giriniz";
    if (!form.tiktok_username.trim()) e.tiktok_username = "TikTok kullanıcı adı zorunludur";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Geçerli bir e-posta adresi giriniz";
    if (!form.kvkk) e.kvkk = "Devam etmek için KVKK metnini onaylamanız gerekir";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/applications", {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email,
        tiktok_username: form.tiktok_username,
        why_us: form.why_us,
        kvkk_accepted: form.kvkk,
      });
      setSuccess(true);
      toast.success(res.data.message);
      setForm({ full_name: "", phone: "", email: "", tiktok_username: "", why_us: "", kvkk: false });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (key) =>
    `w-full bg-black/40 border rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-[#64748B] outline-none transition-colors focus:border-cyan-400/60 ${
      errors[key] ? "border-red-500/60" : "border-white/10"
    }`;

  return (
    <section id="basvuru" data-testid="apply-section" className="relative py-28 bg-[#070A12] overflow-hidden">
      <div className="absolute top-20 right-0 w-[420px] h-[420px] rounded-full bg-[#0047FF]/10 blur-[130px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[320px] h-[320px] rounded-full bg-[#00F0FF]/8 blur-[110px] pointer-events-none" />

      <div className="max-w-3xl mx-auto px-5 lg:px-8 relative">
        <Reveal>
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Ajansa Başvur</span>
            <h2 className="font-display font-700 text-3xl md:text-4xl mt-4 text-white tracking-tight">
              Hoom Agency Ailesine <span className="text-gradient">Katıl</span>
            </h2>
            <p className="mt-5 text-[#A0ABC0]">
              Formu doldur, ekibimiz en kısa sürede seninle iletişime geçsin.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="rounded-3xl border border-white/[0.08] bg-[#0A0E17]/90 backdrop-blur p-7 md:p-10">
            {success ? (
              <div className="text-center py-10" data-testid="apply-success-message">
                <CheckCircle2 size={48} strokeWidth={1.5} className="text-cyan-400 mx-auto mb-5" />
                <h3 className="font-display font-700 text-lg text-white mb-3">Başvurunuz Alındı</h3>
                <p className="text-sm text-[#A0ABC0] max-w-md mx-auto leading-relaxed">
                  Başvurunuz başarıyla alınmıştır. Ekibimiz en kısa sürede sizinle iletişime geçecektir.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  data-testid="apply-new-button"
                  className="mt-8 text-sm font-semibold text-cyan-300 hover:text-cyan-100 transition-colors"
                >
                  Yeni başvuru oluştur
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="apply-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">
                      Ad Soyad <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      data-testid="apply-fullname-input"
                      className={inputCls("full_name")}
                      placeholder="Adınız Soyadınız"
                      value={form.full_name}
                      onChange={(e) => set("full_name", e.target.value)}
                    />
                    {errors.full_name && <p data-testid="apply-fullname-error" className="mt-1.5 text-xs text-red-400">{errors.full_name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">
                      Telefon Numarası <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      data-testid="apply-phone-input"
                      className={inputCls("phone")}
                      placeholder="05xx xxx xx xx"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                    />
                    {errors.phone && <p data-testid="apply-phone-error" className="mt-1.5 text-xs text-red-400">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">E-posta Adresi</label>
                    <input
                      data-testid="apply-email-input"
                      type="email"
                      className={inputCls("email")}
                      placeholder="ornek@mail.com"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                    />
                    {errors.email && <p data-testid="apply-email-error" className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">
                      TikTok Kullanıcı Adı <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      data-testid="apply-tiktok-input"
                      className={inputCls("tiktok_username")}
                      placeholder="@kullaniciadi"
                      value={form.tiktok_username}
                      onChange={(e) => set("tiktok_username", e.target.value)}
                    />
                    {errors.tiktok_username && <p data-testid="apply-tiktok-error" className="mt-1.5 text-xs text-red-400">{errors.tiktok_username}</p>}
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Neden bizi tercih ediyorsun?</label>
                  <textarea
                    data-testid="apply-whyus-input"
                    rows={4}
                    className={`${inputCls("why_us")} resize-none`}
                    placeholder="Kısaca kendinden ve hedeflerinden bahsedebilirsin..."
                    value={form.why_us}
                    onChange={(e) => set("why_us", e.target.value)}
                  />
                </div>

                <div className="mt-6">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="apply-kvkk-checkbox"
                      checked={form.kvkk}
                      onChange={(e) => set("kvkk", e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-white/20 bg-black/40 accent-cyan-400"
                    />
                    <span className="text-xs text-[#A0ABC0] leading-relaxed">
                      KVKK Aydınlatma Metni'ni okudum ve iletişim bilgilerimin başvuru sürecinde kullanılmasını kabul ediyorum.
                    </span>
                  </label>
                  {errors.kvkk && <p data-testid="apply-kvkk-error" className="mt-1.5 text-xs text-red-400">{errors.kvkk}</p>}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="apply-submit-button"
                  className="btn-glow mt-8 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white py-4 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send size={16} strokeWidth={1.5} />
                      Başvuruyu Gönder
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
